import { type ClassValue, clsx } from 'clsx';
import type { Timestamp } from 'firebase/firestore';
import { badgeClassName, type BadgeVariant } from '@/components/ui/badge';
import type { AuditEffectiveConfig } from '@/types/models';
import type { Locale } from './i18n';
import type { messages as MSG } from './i18n.messages';

type StatusKey = keyof typeof MSG;

export type TranslateFn = (
  key: keyof typeof MSG,
  vars?: Record<string, string | number>,
) => string;

export const cn = (...x: ClassValue[]) => clsx(x);

export function fmtBytes(b?: number): string {
  if (b == null) return '—';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.max(b, 1)) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export function fmtDate(
  ts?: Timestamp | Date | string | number | null,
  locale: Locale = 'fr',
): string {
  if (!ts) return '—';
  let d: Date;
  if (ts instanceof Date) d = ts;
  else if (typeof ts === 'string' || typeof ts === 'number') d = new Date(ts);
  else if ('toDate' in ts) d = ts.toDate();
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  const tag = locale === 'en' ? 'en-CA' : 'fr-CA';
  return d.toLocaleString(tag, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Relative time via i18n keys (`time.*`). Pass `t` and `locale` from `useT()`. */
export function fmtRelative(
  ts: Timestamp | Date | null | undefined,
  t: TranslateFn,
  locale: Locale,
): string {
  if (!ts) return '—';
  const d = ts instanceof Date ? ts : ts.toDate();
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return t('time.just_now');
  if (diff < 3600) return t('time.min_ago', { n: Math.floor(diff / 60) });
  if (diff < 86400) return t('time.h_ago', { n: Math.floor(diff / 3600) });
  if (diff < 86400 * 30) return t('time.d_ago', { n: Math.floor(diff / 86400) });
  return fmtDate(d, locale);
}

export function statusBadgeVariant(s?: string): BadgeVariant {
  switch (s) {
    case 'ready':
    case 'success':
    case 'accepted':
      return 'success';
    case 'processing':
    case 'uploading':
    case 'pending':
    case 'draft':
      return 'warning';
    case 'failed':
    case 'expired':
    case 'revoked':
      return 'danger';
    case 'submitted':
      return 'info';
    default:
      return 'neutral';
  }
}

export function statusColor(s?: string): string {
  return badgeClassName(statusBadgeVariant(s));
}

/**
 * Mapping from raw Firestore status strings to i18n keys.
 * Use with `t(STATUS_KEY[status])` for localized labels.
 */
export const STATUS_KEY: Record<string, StatusKey> = {
  uploading:  'status.uploading',
  processing: 'status.processing',
  ready:      'status.ready',
  failed:     'status.failed',
  pending:    'status.pending',
  accepted:   'status.accepted',
  expired:    'status.expired',
  revoked:    'status.revoked',
  draft:      'status.draft',
  submitted:  'status.submitted',
};

// ─── Audit (contrat 1.0) ──────────────────────────────────────────────────

/**
 * Sidecars NDJSON du module 07 : ils héritent du statut du module, on ne les
 * liste pas séparément. Les agrégats calculés à l'ingestion
 * (`07_filesystem_summary`, `09_remote_files_summary`) ne sont pas non plus
 * des modules de collecte : proposer `auditai --modules 07` pour eux n'aurait
 * aucun sens.
 */
const MODULE_SIDECARS = new Set([
  '07_filesystem_files', '07_filesystem_dirs',
  '07_filesystem_summary', '09_remote_files_summary',
]);
/**
 * Sidecar NDJSON du module 09 : `09_files_<hash8>`. Le suffixe est hexadécimal,
 * donc il ressemble à un module (`09_files_abcdefab`) environ une fois sur
 * 2 700 — assez pour qu'un jour un audit propose `auditai --modules 09` à cause
 * d'un sidecar. La règle est explicite plutôt que probabiliste.
 */
const REMOTE_FILES_SIDECAR_RE = /^09_files_[0-9a-f]{8}$/;
const isModuleSidecar = (key: string) =>
  MODULE_SIDECARS.has(key) || REMOTE_FILES_SIDECAR_RE.test(key);
const MODULE_KEY_RE = /^(\d{2})_[a-z_]+$/;
/** Docs « modules » qui ne sont pas des modules de collecte (rapports du pipeline : 00_executive_summary). */
const NON_COLLECT_PREFIX = '00_';
/**
 * Rapports du pipeline numérotés comme un module : `09_preconisations.md`
 * (contrat 1.2) porte le statut du pipeline, pas celui d'un collecteur —
 * proposer `auditai --modules 09` n'aurait aucun sens.
 */
const PIPELINE_REPORT_KEYS = new Set(['09_preconisations']);

/**
 * Clés des modules dont le statut n'est pas `success` (échec, ignoré,
 * interrompu), triées, hors sidecars NDJSON, hors rapports du pipeline
 * (`00_*`, `09_preconisations`, statut = run_metadata.results.pipeline) et
 * hors docs non-modules (`data_catalog`, `recommendations`).
 */
export function failedModuleKeys(modules: Record<string, { status?: string } | undefined>): string[] {
  return Object.entries(modules)
    .filter(([key, m]) => MODULE_KEY_RE.test(key)
      && !key.startsWith(NON_COLLECT_PREFIX)
      && !isModuleSidecar(key)
      && !PIPELINE_REPORT_KEYS.has(key)
      && m?.status !== 'success')
    .map(([key]) => key)
    .sort();
}

/** `01_hardware_compute` → `01` (identifiant attendu par `auditai --modules`). */
export function moduleId(key: string): string {
  return MODULE_KEY_RE.exec(key)?.[1] ?? key;
}

/** Commande de relance à afficher : `auditai --modules 01 03`. */
export function rerunCommand(keys: string[]): string {
  return `auditai --modules ${keys.map(moduleId).join(' ')}`;
}

/** Nombre de jours entiers écoulés depuis `ts` (0 si absent / invalide). */
export function daysSince(ts?: Timestamp | Date | null, now: Date = new Date()): number {
  if (!ts) return 0;
  const d = ts instanceof Date ? ts : ts.toDate();
  const ms = now.getTime() - d.getTime();
  return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 86400000) : 0;
}

/** Collecte de plus de `maxDays` jours (30 par défaut) → badge « collecte ancienne ». */
export function isStaleCollection(ts?: Timestamp | Date | null, maxDays = 30, now: Date = new Date()): boolean {
  return daysSince(ts, now) > maxDays;
}

/** Formatte un volume en Go (1 décimale, sans zéro inutile) ; `—` si absent. */
export function fmtGb(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1)} GB`;
}

// ─── 07_filesystem_map.json (contrats 1.0 et 1.1) ────────────────────────

/** Racine parcourue : `scanned_roots_detail` (1.1) ou `scanned_roots` (1.0, sans type de montage). */
export interface FilesystemRoot { path: string; kind: string | null; fstype: string | null }
/** Une entrée de `stats_by_category` (1.1 ajoute `other`). */
export interface FilesystemCategoryStat { category: string; count: number; sizeBytes: number }
/** Une entrée de `unknown_extensions_top` (1.1). */
export interface FilesystemExtensionStat { ext: string; count: number; sizeBytes: number }

/**
 * Compteurs de `walk_stats` (1.1). `null` = clé absente (audit 1.0 / legacy) :
 * on n'affiche jamais un 0 inventé.
 */
export interface FilesystemWalkCounters {
  statErrors: number | null;
  unreadableDirs: number | null;
  symlinksSkipped: number | null;
  prunedDirs: number | null;
  encodingIssues: number | null;
}

export interface FilesystemMapMeta extends FilesystemWalkCounters {
  scannedRoots: string[];
  roots: FilesystemRoot[];
  truncated: boolean;
  /** `partial` (1.1) ou budget de temps épuisé sur au moins une racine. */
  partial: boolean;
  budgetExhausted: string[];
  categories: FilesystemCategoryStat[];
  unknownExtensions: FilesystemExtensionStat[];
}

const strList = (v: unknown): string[] =>
  (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** Entier fini ou `null` (chaîne, booléen, absent → `null`). */
const intOrNull = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

const numOr0 = (v: unknown): number => intOrNull(v) ?? 0;

/**
 * Métadonnées de `07_filesystem_map.json`, tolérantes aux contrats 1.0 et 1.1 :
 * racines (avec type de montage en 1.1), `truncated`, parcours partiel /
 * budget épuisé, statistiques par catégorie (dont `other`), extensions
 * inconnues et compteurs d'erreurs du parcours.
 */
export function filesystemMapMeta(json: unknown): FilesystemMapMeta {
  const j = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  const detail = Array.isArray(j.scanned_roots_detail) ? j.scanned_roots_detail : [];
  const roots: FilesystemRoot[] = detail
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map(r => ({
      path: typeof r.path === 'string' ? r.path : '',
      kind: typeof r.kind === 'string' ? r.kind : null,
      fstype: typeof r.fstype === 'string' ? r.fstype : null,
    }))
    .filter(r => r.path !== '');
  const scannedRoots = strList(j.scanned_roots);
  const budgetExhausted = strList(j.budget_exhausted);
  const stats = (j.stats_by_category && typeof j.stats_by_category === 'object' ? j.stats_by_category : {}) as Record<string, unknown>;
  const categories: FilesystemCategoryStat[] = Object.entries(stats)
    .map(([category, v]) => {
      const c = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
      return { category, count: numOr0(c.count), sizeBytes: numOr0(c.size_bytes) };
    })
    .filter(c => c.count > 0 || c.sizeBytes > 0)
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
  const unknownExtensions: FilesystemExtensionStat[] = (Array.isArray(j.unknown_extensions_top) ? j.unknown_extensions_top : [])
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map(e => ({ ext: typeof e.ext === 'string' ? e.ext : '', count: numOr0(e.count), sizeBytes: numOr0(e.size_bytes) }));
  const walk = (j.walk_stats && typeof j.walk_stats === 'object' ? j.walk_stats : {}) as Record<string, unknown>;
  return {
    scannedRoots: scannedRoots.length > 0 ? scannedRoots : roots.map(r => r.path),
    roots: roots.length > 0 ? roots : scannedRoots.map(path => ({ path, kind: null, fstype: null })),
    truncated: j.truncated === true,
    partial: j.partial === true || budgetExhausted.length > 0,
    budgetExhausted,
    categories,
    unknownExtensions,
    statErrors: intOrNull(walk.stat_errors),
    unreadableDirs: intOrNull(walk.unreadable_dirs),
    symlinksSkipped: intOrNull(walk.symlinks_skipped),
    prunedDirs: intOrNull(walk.pruned_dirs),
    encodingIssues: intOrNull(walk.encoding_issues),
  };
}

// ─── Périmètre du run (run_metadata.effective_config, contrat 1.1) ────────

/** Options booléennes de `effective_config` affichées comme puces « option active ». */
export const SCOPE_OPTION_KEYS = ['quick', 'redact', 'netScan', 'diskBenchmark', 'cloudInventory', 'k8sLive'] as const;
export type ScopeOption = typeof SCOPE_OPTION_KEYS[number];

/** Options activées lors du run, dans l'ordre de `SCOPE_OPTION_KEYS` ; `[]` si la config est absente. */
export function activeScopeOptions(cfg?: AuditEffectiveConfig | null): ScopeOption[] {
  if (!cfg) return [];
  return SCOPE_OPTION_KEYS.filter(k => cfg[k] === true);
}

/** Périmètre volontairement réduit : `--quick` ou un budget `--max-minutes`. */
export function isReducedScope(cfg?: AuditEffectiveConfig | null): boolean {
  if (!cfg) return false;
  return cfg.quick === true || (typeof cfg.maxMinutes === 'number' && cfg.maxMinutes > 0);
}

/** Le bloc « Périmètre de l'audit » a quelque chose à montrer (sinon on ne l'affiche pas). */
export function hasScopeDetails(cfg?: AuditEffectiveConfig | null, tags?: Record<string, string> | null): boolean {
  if (tags && Object.keys(tags).length > 0) return true;
  if (!cfg) return false;
  return (cfg.scanRoots?.length ?? 0) > 0
    || (cfg.excludePaths?.length ?? 0) > 0
    || (cfg.modules?.length ?? 0) > 0
    || activeScopeOptions(cfg).length > 0
    || intOrNull(cfg.maxMinutes) != null
    || intOrNull(cfg.maxFiles) != null
    || intOrNull(cfg.maxDepth) != null;
}

/** Débit de lien en Mb/s → « 1 000 Mb/s » / « 10 Gb/s » ; `—` si absent. */
export function fmtMbps(n?: number | null): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return '—';
  return n >= 1000 ? `${Math.round(n / 100) / 10} Gb/s` : `${n} Mb/s`;
}

// ─── Rétention, rôles et suppression ─────────────────────────────────────

/** Durée de conservation des données brutes (zip, raw_logs, NDJSON) ; miroir de AUDIT_RETENTION_DAYS côté fonctions. */
export const AUDIT_RETENTION_DAYS: number = (() => {
  const raw = Number(import.meta.env.VITE_AUDIT_RETENTION_DAYS);
  return Number.isInteger(raw) && raw > 0 ? raw : 30;
})();

type AuditRole = 'admin_global' | 'org_admin' | 'org_member' | undefined;

/** admin_global : tout audit ; org_admin : audits de son org dont status ∈ {failed, uploading}. */
export function canDeleteAudit(role: AuditRole, status?: string): boolean {
  if (role === 'admin_global') return true;
  return role === 'org_admin' && (status === 'failed' || status === 'uploading');
}

/** JSON de module (inline ou json/*) : org_admin et admin_global. */
export function canViewModuleJson(role: AuditRole): boolean {
  return role === 'admin_global' || role === 'org_admin';
}

/** Journaux bruts (raw_logs/*) : admin_global seulement. */
export function canViewRawLog(role: AuditRole): boolean {
  return role === 'admin_global';
}

interface RetentionLike {
  zipPurgedAt?: Timestamp | null;
  rawLogsPurgedAt?: Timestamp | null;
  ndjsonPurgedAt?: Timestamp | null;
}

/** Date de purge la plus récente parmi zip / raw_logs / NDJSON ; `null` si rien n'a été purgé. */
export function latestPurgeDate(retention?: RetentionLike | null): Date | null {
  if (!retention) return null;
  const dates = [retention.zipPurgedAt, retention.rawLogsPurgedAt, retention.ndjsonPurgedAt]
    .filter((x): x is Timestamp => !!x)
    .map(x => x.toDate().getTime())
    .filter(Number.isFinite);
  return dates.length ? new Date(Math.max(...dates)) : null;
}
